import React, { SyntheticEvent, useState } from 'react'
import { notifyError } from '@/components'
import { downloadFileFromUrl } from '@/utils/downloadFile'
import {
  useUploadFeaturesDocsMutation,
  type DeviceUploadedDocument,
  type PendingUploadedDocument,
} from './service'
import { LoadingButton } from '@mui/lab'
import { useParams } from 'react-router-dom'

const MAX_FILE_SIZE = 40 * 1024 * 1024

type Props = {
  documents?: DeviceUploadedDocument[]
  showDownloadAll?: boolean
  onDocumentsUploaded?: (documents: PendingUploadedDocument[]) => void
}

const UploadFeaturesDoc = ({
  documents,
  showDownloadAll = true,
  onDocumentsUploaded,
}: Props) => {
  const { id } = useParams()
  const hasDocuments = documents?.length
  const filesInfo = documents?.map((doc) => ({
    url: doc.url,
    fileName: doc.name || doc.url.split('/').pop() || 'file',
  }))
  const [loading, setLoading] = useState(false)
  const [addIDoc] = useUploadFeaturesDocsMutation()

  const handleAddDoc = async (e: SyntheticEvent) => {
    const target = e.target as HTMLInputElement
    const { files } = target
    if (!files?.length) return

    const oversizedFiles = Array.from(files).filter(
      (file) => file.size > MAX_FILE_SIZE
    )

    if (oversizedFiles.length > 0) {
      notifyError(
        `Следующие файлы превышают допустимый размер 40МБ: ${oversizedFiles
          .map((file) => file.name)
          .join(', ')}`
      )
      target.value = ''
      return
    }

    const fileList = Array.from(files)

    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('deviceId', String(id ?? 0))
      fileList.forEach((file) => {
        formData.append('documents', file)
      })

      const data = await addIDoc(formData).unwrap()
      if (data.success && data.documents?.length) {
        const items: PendingUploadedDocument[] = data.documents.map(
          (docId, i) => ({
            id: docId,
            name: fileList[i]?.name ?? 'файл',
          })
        )
        onDocumentsUploaded?.(items)
      }
    } catch {
      notifyError('Произошла ошибка при загрузке файлов')
    } finally {
      setLoading(false)
      target.value = ''
    }
  }

  const handleDownloadAll = async () => {
    if (!filesInfo?.length) return

    setLoading(true)
    try {
      const downloadPromises = filesInfo.map(async ({ url, fileName }) => {
        await downloadFileFromUrl(url, fileName)
      })

      await Promise.all(downloadPromises)
    } catch (error) {
      console.error('Ошибка при скачивании файлов:', error)
      notifyError('Произошла ошибка при скачивании файлов')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <LoadingButton
        variant={'contained'}
        color='success'
        component='label'
        loading={loading}
      >
        Прикрепить
        <input
          id='docsInput'
          type='file'
          hidden
          multiple
          accept='.jpg, .png, .pdf'
          onChange={handleAddDoc}
        />
      </LoadingButton>
      {Boolean(hasDocuments) && showDownloadAll && (
        <LoadingButton
          variant='contained'
          color='success'
          onClick={handleDownloadAll}
          loading={loading}
        >
          <span>Скачать Все</span>
        </LoadingButton>
      )}
    </>
  )
}

export default UploadFeaturesDoc
